/* eslint-disable */
import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Lock, FileDown, Share2, ListChecks, ArrowRight, ExternalLink, TrendingUp, RefreshCw, Loader2, Eye, History, Sparkles, ShieldCheck, Clock, MapPin, Copy, Zap, Award, AlertOctagon, Edit3 } from "lucide-react";
import Layout from "@/components/Layout";
import PillarBar from "@/components/PillarBar";
import JurisdictionGrid from "@/components/JurisdictionGrid";
import WhyTrustPanel from "@/components/WhyTrustPanel";
import Disclaimer from "@/components/Disclaimer";
import DashboardBanner from "@/components/DashboardBanner";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";
import AuditUsageCounter from "@/components/AuditUsageCounter";
import FeedbackPrompt from "@/components/FeedbackPrompt";
import { InfoTooltip } from "@/components/InfoTooltip";
import TrustSignalsRow from "@/components/TrustSignalsRow";
import StaleAuditBanner from "@/components/StaleAuditBanner";
import ScoreCard from "@/components/ScoreCard";
import ManageJurisdictionsModal, { ALL_STATES } from "@/components/ManageJurisdictionsModal";
import ReferralSection from "@/components/ReferralSection";
import { Button } from "@/components/ui/button";
import { api, BACKEND_URL, getToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { scoreToBand, scoreToColor, scoreToGrade } from "@/lib/scoring";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Area } from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [tier, setTier] = useState("free");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [locked, setLocked] = useState(null); // {reason} when monitoring_locked
  const [openDisputes, setOpenDisputes] = useState(0);
  const [profile, setProfile] = useState(null);
  const [jurisModalOpen, setJurisModalOpen] = useState(false);
  const [jurisSelected, setJurisSelected] = useState([]);
  const [jurisSaving, setJurisSaving] = useState(false);
  const [referral, setReferral] = useState(null);
  const [subTab, setSubTab] = useState("overview");
  const [clearingLegacy, setClearingLegacy] = useState(false);
  const [staleAuditDismissed, setStaleAuditDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem("regulens_stale_audit_dismissed_until");
      if (!raw) return false;
      return new Date(raw).getTime() > Date.now();
    } catch { return false; }
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit/me/report");
      if (data.monitoring_locked) {
        setLocked({ reason: data.monitoring_locked_reason });
        setReport(null);
      } else {
        setReport(data.report);
        setTier(data.tier || "free");
        if (data.tier && data.tier !== "free") {
          const h = await api.get("/audit/me/history");
          setHistory(h.data.history || []);
        }
      }
      // Side-load: disputes (for provisional label + Under Review state) + profile + referral
      try {
        const d = await api.get("/audit/me/disputes");
        setOpenDisputes(d.data.open_count || 0);
      } catch {}
      try {
        const p = await api.get("/audit/me/profile");
        setProfile(p.data.profile || null);
        setJurisSelected(p.data.profile?.selected_jurisdictions || []);
      } catch {}
      if (data.tier === "agency") {
        try {
          const r = await api.get("/referral/me");
          setReferral(r.data);
        } catch {}
      }
    } finally { setLoading(false); }
  };

  // Intentional mount-only fetch.
  useEffect(() => { load(); }, []);

  const refreshHistory = async () => {
    try {
      const h = await api.get("/audit/me/history");
      setHistory(h.data.history || []);
    } catch {}
  };

  const handleClearLegacy = async () => {
    const legacyCount = history.filter((h) => !h.has_detail).length;
    if (!legacyCount) return;
    const ok = window.confirm(
      `Remove ${legacyCount} old test ${legacyCount === 1 ? "entry" : "entries"} with no detail available? ` +
      `Audits with full input & result detail are kept. This cannot be undone.`
    );
    if (!ok) return;
    setClearingLegacy(true);
    try {
      const { data } = await api.delete("/audit/me/history/legacy");
      await refreshHistory();
      toast.success(`Cleared ${data.deleted_count} old test ${data.deleted_count === 1 ? "entry" : "entries"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear old entries.");
    } finally {
      setClearingLegacy(false);
    }
  };

  const downloadPdf = () => {
    // Browser-native: window.print() respects @media print rules and
    // lets the user "Save as PDF" via the print dialog.
    document.body.classList.add("printing");
    const suggested = `Regulens-Readiness-${(report?.store_url || "report").replace(/[^a-z0-9.-]/gi, "-")}-${new Date().toISOString().slice(0, 10)}`;
    const prevTitle = document.title;
    document.title = suggested;
    window.print();
    // Restore after the dialog closes
    setTimeout(() => {
      document.body.classList.remove("printing");
      document.title = prevTitle;
    }, 1500);
  };

  const createShare = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/share/create");
      const url = `${window.location.origin}/report/${data.report_id}`;
      navigator.clipboard.writeText(url);
      toast.success("Shareable link copied to clipboard (expires in 30 days)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create share link"); } finally { setCreating(false); }
  };

  const [refreshing, setRefreshing] = useState(false);

  /**
   * Re-run the audit with the merchant's currently-stored inputs — no
   * onboarding round-trip. When the scoring engine version has bumped
   * since the last run this executes for free (no quota consumed).
   * Otherwise it consumes one slot from the monthly quota.
   */
  const rerunSameInputs = async () => {
    if (!profile || refreshing) return;
    if (!window.confirm(
      "Re-run your Readiness audit using the same inputs you previously submitted?\n\n"
      + "If the scoring engine has been updated since your last audit, this refresh is free. "
      + "Otherwise it consumes one audit from your monthly quota.",
    )) return;
    setRefreshing(true);
    try {
      const payload = {
        shopify_store_url: profile.shopify_store_url,
        annual_revenue_tier: profile.annual_revenue_tier,
        annual_transaction_tier: profile.annual_transaction_tier,
        score_mode: profile.score_mode,
        selected_jurisdictions: profile.selected_jurisdictions,
        operational_focus: profile.operational_focus,
        ny_annual_revenue: profile.ny_annual_revenue,
        ny_annual_transactions: profile.ny_annual_transactions,
        per_state_data: profile.per_state_data,
        privacy_policy_text: profile.privacy_policy_text,
        terms_of_service_text: profile.terms_of_service_text,
        refund_policy_text: profile.refund_policy_text,
        cookie_consent_present: profile.cookie_consent_present,
        privacy_contact_present: profile.privacy_contact_present,
        retention_policy_present: profile.retention_policy_present,
        jurisdictions_identified: profile.jurisdictions_identified,
        nexus_documented: profile.nexus_documented,
        active_monitoring: profile.active_monitoring,
      };
      const { data } = await api.post("/audit/run", payload);
      const jobId = data.job_id;
      for (let i = 0; i < 60; i++) {
        const s = await api.get(`/audit/status/${jobId}`);
        if (s.data.status === "complete") {
          toast.success(s.data.cached ? "Score refreshed (unchanged)." : "Score refreshed.");
          window.location.reload();
          return;
        }
        if (s.data.status === "failed") {
          toast.error(s.data.error_message || "Refresh failed.");
          return;
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      toast.error("Refresh timed out — please try again in a minute.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed.");
    } finally {
      setRefreshing(false);
    }
  };

  // Quick-Win: highest-impact single recommendation if composite < 60.
  // IMPORTANT: useMemo must be called unconditionally on every render —
  // do NOT move below the early-return guards below.
  const recs = (report?.remediationItems || report?.readinessRecommendations || []);
  const topRec = useMemo(() => {
    if (!recs.length) return null;
    return [...recs].sort((a, b) => (b.potentialGain || 0) - (a.potentialGain || 0))[0];
  }, [recs]);

  // Pillar-2 UX clarity — enumerate the merchant's selected states that are
  // NOT yet in the covered-scoring list so an N/A pillar reads as
  // "coming soon" rather than "is something broken?".
  const PILLAR2_COVERED = useMemo(() => new Set(["NY", "TX", "FL", "DC"]), []);
  const STATE_NAMES = useMemo(() => ({
    AL:"Alabama",AK:"Alaska",AZ:"Arizona",AR:"Arkansas",CA:"California",CO:"Colorado",CT:"Connecticut",
    DE:"Delaware",DC:"District of Columbia",FL:"Florida",GA:"Georgia",HI:"Hawaii",ID:"Idaho",IL:"Illinois",
    IN:"Indiana",IA:"Iowa",KS:"Kansas",KY:"Kentucky",LA:"Louisiana",ME:"Maine",MD:"Maryland",MA:"Massachusetts",
    MI:"Michigan",MN:"Minnesota",MS:"Mississippi",MO:"Missouri",MT:"Montana",NE:"Nebraska",NV:"Nevada",
    NH:"New Hampshire",NJ:"New Jersey",NM:"New Mexico",NY:"New York",NC:"North Carolina",ND:"North Dakota",
    OH:"Ohio",OK:"Oklahoma",OR:"Oregon",PA:"Pennsylvania",RI:"Rhode Island",SC:"South Carolina",SD:"South Dakota",
    TN:"Tennessee",TX:"Texas",UT:"Utah",VT:"Vermont",VA:"Virginia",WA:"Washington",WV:"West Virginia",
    WI:"Wisconsin",WY:"Wyoming",
  }), []);
  const pillar2UncoveredNote = useMemo(() => {
    const selected = profile?.selected_jurisdictions || report?.selected_jurisdictions || [];
    const uncovered = selected.filter((abbr) => !PILLAR2_COVERED.has(abbr));
    if (uncovered.length === 0) return null;
    const names = uncovered.map((a) => STATE_NAMES[a] || a);
    const list = names.length === 1
      ? names[0]
      : names.length === 2
        ? `${names[0]} and ${names[1]}`
        : `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
    return `${list} ${uncovered.length === 1 ? "is" : "are"} not yet in the covered state list above — comprehensive per-state privacy scoring is expanding soon. Until then, your Privacy Law Readiness for ${uncovered.length === 1 ? "this state" : "these states"} shows as N/A rather than defaulting to a score that might not reflect reality.`;
  }, [profile, report, PILLAR2_COVERED, STATE_NAMES]);

  // Stage 3 (Prompt B) — surface the Provisional state so the Pillar 2
  // card can render a "Complete your compliance profile" CTA. Detected
  // either from the pillar-level status field (both selected states
  // Provisional → whole pillar Provisional) OR from any individual
  // per-state breakdown being marked provisional.
  const isPillar2Provisional = useMemo(() => {
    const p2d = report?.pillar2_detail || {};
    if (p2d.status === "provisional") return true;
    const bd = p2d.breakdown || {};
    return Object.values(bd).some((b) => b && b.status === "provisional");
  }, [report]);

  // 2026-07-15 CA/VA/CO activation — compute the specific list of Prompt-B
  // pilot states that are provisional for THIS merchant, so the banner can
  // name them explicitly instead of saying the vague "your selected state(s)".
  const promptBProvisionalStates = useMemo(() => {
    const bd = (report?.pillar2_detail || {}).breakdown || {};
    return ["CA", "VA", "CO"].filter((abbr) => bd[abbr]?.status === "provisional");
  }, [report]);

  // Session 2b (2026-07-16) — Signal Trust roll-up chip.
  // Reads profile.privacy_signals (populated by the 2a backfill + the new
  // 2b onboarding review step) and reports which extraction path drove the
  // merchant's currently-scored privacy signals.
  //   * "Merchant reviewed" — at least one signal has source in
  //     (merchant_confirmed, merchant_overridden, merchant_answered).
  //     Means the merchant went through the 2b review flow → highest trust.
  //   * "AI detected"      — signals present, but none merchant-touched.
  //   * "Legacy match"     — only source=legacy_keyword_match signals present.
  //     Means the merchant hasn't been through the 2b flow yet.
  //   * null               — no privacy_signals at all (pre-2a profile).
  const signalTrust = useMemo(() => {
    const ps = profile?.privacy_signals || {};
    if (!ps || Object.keys(ps).length === 0) return null;
    const sources = Object.values(ps)
      .map((s) => (s && typeof s === "object" ? s.source : null))
      .filter(Boolean);
    if (sources.length === 0) return null;
    const merchantTouched = sources.filter((s) =>
      ["merchant_confirmed", "merchant_overridden", "merchant_answered"].includes(s)
    ).length;
    const aiOnly = sources.filter((s) => s === "ai_extracted").length;
    const legacyOnly = sources.filter((s) => s === "legacy_keyword_match").length;
    if (merchantTouched > 0) {
      return { label: "Merchant reviewed", color: "var(--rg-sage)", detail: `${merchantTouched} of ${sources.length} rights confirmed by you` };
    }
    if (aiOnly > 0) {
      return { label: "AI detected", color: "var(--rg-sage)", detail: "Pending your review — visit Onboarding to confirm" };
    }
    if (legacyOnly > 0) {
      return { label: "Legacy match", color: "var(--rg-amber)", detail: "Auto-detected from policy keywords — accuracy varies. Confirm via Onboarding for a more precise score." };
    }
    return null;
  }, [profile]);

  // P10 — Stale audit (>30 days). MUST live above early returns to keep
  // hook call order stable across renders.
  const lastAuditDays = useMemo(() => {
    if (!report?.audit_date) return null;
    const ts = new Date(report.audit_date).getTime();
    if (isNaN(ts)) return null;
    return Math.floor((Date.now() - ts) / (24 * 60 * 60 * 1000));
  }, [report?.audit_date]);

  if (loading) return (
    <Layout><div className="max-w-7xl mx-auto px-6 py-20 text-center"><Loader2 className="w-8 h-8 text-[var(--rg-sage)] animate-spin mx-auto"/></div></Layout>
  );

  if (locked) {
    return (
      <Layout>
        <section className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="dashboard-locked">
          <Lock className="w-12 h-12 text-[var(--rg-amber)] mx-auto mb-3" />
          <h1 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-3">Dashboard access suspended</h1>
          <p className="text-[var(--rg-text)] mb-6">{locked.reason}</p>
          <Link to="/onboarding"><Button className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold">Re-verify Store <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
        </section>
      </Layout>
    );
  }

  if (!report) {
    return (
      <Layout>
        <section className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="dashboard-empty-state">
          <VerifyEmailBanner />
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">Welcome</div>
          {/* Item 9 — Free-tier empty-state copy */}
          <h1
            className="font-heading font-extrabold text-3xl sm:text-4xl text-[var(--rg-text-strong)] mb-3"
            data-testid="empty-state-heading"
          >
            You are 3 minutes away from knowing your compliance exposure.
          </h1>
          <p
            className="text-[var(--rg-text)] mb-8 max-w-xl mx-auto"
            data-testid="empty-state-subheading"
          >
            Find out which U.S. states you may have sales tax or privacy law
            obligations in — based on your actual revenue and transaction data.
          </p>
          <Link to="/onboarding">
            <Button
              size="lg"
              className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-bold"
              data-testid="empty-state-cta"
            >
              Start My Free Audit
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>

          {/* Three trust signals (Item 9) */}
          <div
            className="mt-10 grid sm:grid-cols-3 gap-4 text-left max-w-2xl mx-auto"
            data-testid="empty-state-trust-signals"
          >
            <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4 flex items-start gap-2">
              <ShieldCheck className="w-5 h-5 text-[var(--rg-sage)] mt-0.5" />
              <div className="text-xs text-[var(--rg-text)]">Based on verified government sources</div>
            </div>
            <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4 flex items-start gap-2">
              <Clock className="w-5 h-5 text-[var(--rg-sage)] mt-0.5" />
              <div className="text-xs text-[var(--rg-text)]">No credit card required</div>
            </div>
            <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4 flex items-start gap-2">
              <Eye className="w-5 h-5 text-[var(--rg-sage)] mt-0.5" />
              <div className="text-xs text-[var(--rg-text)]">Used by Shopify merchants across 50 tax jurisdictions and 15 privacy-scored states</div>
            </div>
          </div>

          {/* Session 2b — surface the signal-trust chip on empty-state too so
              merchants who just finished the rights-review step see the
              confirmation of their work before they run their first audit. */}
          {signalTrust && (
            <div className="mt-6 flex justify-center" data-testid="empty-state-signal-trust-wrap">
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded"
                style={{ background: signalTrust.color, color: "var(--rg-bg)" }}
                title={signalTrust.detail}
                data-testid="dashboard-signal-trust-badge"
              >
                Signal quality: {signalTrust.label}
              </span>
            </div>
          )}

          {/* Referral section is visible in empty-state too for Agency-tier users */}
          {tier === "agency" && <ReferralSection referral={referral} variant="empty" />}
        </section>
      </Layout>
    );
  }

  // Free-tier paywall (separate from monitoring-lock above)
  const lockedFreeReport = report.is_locked_pro;
  const isPaid = ["solo", "growth", "professional", "agency", "pro"].includes(tier);

  const composite = report.composite_score || 0;
  const scoreMode = (profile?.score_mode || "estimated").toLowerCase();
  const isVerifiedScore = scoreMode === "verified";
  const showQuickWin = composite < 60 && topRec;
  const showShareBadge =
    composite > 75 &&
    profile &&
    !profile.share_badge_disabled &&
    isVerifiedScore &&
    ["growth", "professional", "agency", "pro"].includes(tier);

  // P10 — Stale audit reminder (more than 30 days since last audit)
  const showStaleBanner = lastAuditDays != null && lastAuditDays > 30 && !staleAuditDismissed;
  const dismissStaleBanner = () => {
    const until = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
    try { localStorage.setItem("regulens_stale_audit_dismissed_until", until); } catch {}
    setStaleAuditDismissed(true);
  };

  const toggleJuris = (abbr) => {
    setJurisSelected((s) => s.includes(abbr) ? s.filter((x) => x !== abbr) : [...s, abbr]);
  };

  const saveJurisdictions = async () => {
    if (jurisSelected.length === 0) return toast.error("At least one jurisdiction must be selected.");
    setJurisSaving(true);
    try {
      await api.post("/audit/me/jurisdictions", { selected_jurisdictions: jurisSelected });
      toast.success("Jurisdictions updated. Run a fresh audit to see the new score.");
      setJurisModalOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setJurisSaving(false);
    }
  };

  const copyBadgeSnippet = () => {
    const shareUrl = `${window.location.origin}/dashboard`;
    const snippet = `<a href="${shareUrl}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:6px;padding:6px 12px;border:1px solid #6B8471;border-radius:4px;background:#F7F6F3;color:#5A7361;font-family:system-ui,sans-serif;font-size:12px;font-weight:600;text-decoration:none">✓ Compliance readiness checked by Regulens</a>`;
    navigator.clipboard.writeText(snippet);
    toast.success("Badge HTML copied to clipboard");
  };

  return (
    <Layout fullWidth>
      <Toaster theme="dark" position="top-right" offset={80} />
      <section className="max-w-7xl mx-auto px-6 py-10">
        <VerifyEmailBanner />
        <DashboardBanner />
        <FeedbackPrompt />
        <div className="mb-6"><AuditUsageCounter /></div>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)]">Readiness Dashboard</div>
            <h1 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl text-[var(--rg-text-strong)] mt-2">{report.store_url}</h1>
            <div className="text-xs text-[var(--rg-muted)] font-mono mt-1">Last audited: {(report.audit_date || "").slice(0, 10)} · Data verified: {report.dataLastVerified}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/remediation"><Button variant="outline" className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]" data-testid="btn-remediation"><ListChecks className="w-4 h-4 mr-2" /> Remediation</Button></Link>
            <Button onClick={() => setJurisModalOpen(true)} variant="outline" className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]" data-testid="btn-manage-jurisdictions">
              <MapPin className="w-4 h-4 mr-2" /> Manage My Jurisdictions
            </Button>
            <Button onClick={downloadPdf} disabled={!["growth","professional","agency","pro"].includes(tier)} variant="outline" className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]" data-testid="btn-download-pdf"><FileDown className="w-4 h-4 mr-2" /> ↓ Export Readiness Report as PDF {!["growth","professional","agency","pro"].includes(tier) && <Lock className="w-3 h-3 ml-1" />}</Button>
            <Button onClick={createShare} disabled={tier !== "pro" || creating} variant="outline" className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]" data-testid="btn-share"><Share2 className="w-4 h-4 mr-2" /> Share {tier !== "pro" && <Lock className="w-3 h-3 ml-1" />}</Button>
            <Button
              onClick={rerunSameInputs}
              disabled={!profile || refreshing}
              variant="outline"
              className="border-[var(--rg-sage)] text-[var(--rg-sage)] hover:bg-[var(--rg-sage)]/10"
              data-testid="btn-refresh-score"
              title="Re-runs your audit using the exact inputs you previously submitted. Free when scoring rules have updated; otherwise consumes one audit from your monthly quota."
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing…" : "Refresh Score"}
            </Button>
            <Link to="/onboarding"><Button variant="outline" className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]" data-testid="btn-rerun"><Edit3 className="w-4 h-4 mr-2" /> Re-run with Edits</Button></Link>
          </div>
        </div>

        {/* P9 — trust signals row below the action buttons */}
        <TrustSignalsRow testId="dashboard-trust-row" />

        {/* P10 — stale audit reminder banner (30-day soft nudge) */}
        {showStaleBanner && lastAuditDays <= 90 && (
          <StaleAuditBanner days={lastAuditDays} onDismiss={dismissStaleBanner} />
        )}

        {/* Item 7 — 90-day persistent banner (cannot be dismissed; only clears
            after the user runs a fresh audit). Only shown to merchants with
            at least one prior audit (`report` exists). Never shown to brand-new
            free-tier users — they see the empty-state instead. */}
        {lastAuditDays != null && lastAuditDays > 90 && (
          <div
            className="rounded-md border-2 border-[var(--rg-red)] bg-[var(--rg-red-soft)] px-4 py-3 mb-6 flex flex-wrap items-center gap-3"
            data-testid="ninety-day-stale-banner"
            role="alert"
          >
            <AlertOctagon className="w-5 h-5 text-[var(--rg-red)] shrink-0" />
            <div className="text-sm text-[var(--rg-text-strong)] flex-1 min-w-[260px]">
              <span className="font-bold">
                Your last compliance audit was over 90 days ago.
              </span>{" "}
              Regulations change frequently — your score may no longer reflect
              current requirements. Run a fresh audit to stay current.
            </div>
            <Link to="/onboarding">
              <Button
                size="sm"
                className="bg-[var(--rg-red)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-red)] font-bold"
                data-testid="ninety-day-stale-cta"
              >
                Run New Audit
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </div>
        )}

        {/* sub-tabs: Overview / Audit History (paid only) */}
        {isPaid && (
          <div className="flex gap-2 mb-6" data-testid="dashboard-sub-tabs">
            <Button size="sm" onClick={() => setSubTab("overview")} className={subTab === "overview" ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)]" : "bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)]"} data-testid="dashboard-tab-overview">
              Overview
            </Button>
            <Button size="sm" onClick={() => setSubTab("history")} className={subTab === "history" ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)]" : "bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)]"} data-testid="dashboard-tab-history">
              <History className="w-4 h-4 mr-1.5" /> Audit History
            </Button>
          </div>
        )}

        {subTab === "history" && isPaid && (
          <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5" data-testid="audit-history-panel">
            <div className="flex items-center justify-between gap-4 mb-4">
              <h2 className="font-heading font-bold text-xl text-[var(--rg-text-strong)]">Audit History</h2>
              {history.some((h) => !h.has_detail) && (
                <Button
                  size="sm"
                  onClick={handleClearLegacy}
                  disabled={clearingLegacy}
                  className="bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-bg)]"
                  data-testid="clear-legacy-history-btn"
                >
                  {clearingLegacy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <AlertOctagon className="w-4 h-4 mr-1.5" />}
                  Clear old test entries
                </Button>
              )}
            </div>
            {history.length === 0 ? (
              <p className="text-sm text-[var(--rg-text)]">No previous audits yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)] border-b border-[var(--rg-border)]">
                      <th className="py-2 px-2">Date</th>
                      <th className="py-2 px-2">Score</th>
                      <th className="py-2 px-2">Grade</th>
                      <th className="py-2 px-2">Trigger</th>
                      <th className="py-2 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...history].reverse().map((h) => (
                      <tr
                        key={h.id}
                        className="border-b border-[var(--rg-border)]/60 cursor-pointer transition-fast hover:bg-[var(--rg-bg)]"
                        onClick={() => navigate(`/dashboard/history/${h.id}`)}
                        data-testid={`audit-history-row-${h.id}`}
                      >
                        <td className="py-2 px-2 text-[var(--rg-text)] font-mono text-xs">{(h.audit_date || "").slice(0, 19)}</td>
                        <td className="py-2 px-2 font-bold" style={{ color: scoreToColor(h.composite_score) }}>{h.composite_score}</td>
                        <td className="py-2 px-2 text-[var(--rg-text)] font-mono">{h.score_grade}</td>
                        <td className="py-2 px-2 text-[var(--rg-text)] text-xs">{h.trigger_reason}</td>
                        <td className="py-2 px-2 text-right">
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--rg-sage)]">
                            View <ArrowRight className="w-3.5 h-3.5" />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {subTab === "overview" && (
        <>
        {/* Score + pillars */}
        <div className="grid lg:grid-cols-3 gap-6">
          <ScoreCard
            score={report.composite_score}
            openDisputes={openDisputes}
            scoreMode={scoreMode}
            storeUrlUnverified={!!report.store_url_unverified}
            engineVersion={report.scoring_engine_version ?? null}
            auditDate={report.audit_date || profile?.last_audit_date || null}
          />

          <div className="lg:col-span-2 rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 space-y-6">
            <PillarBar label={<>Sales Tax Nexus <InfoTooltip testId="tooltip-pillar-1" text="Based on your sales volume compared to publicly available state revenue thresholds." /></>} score={report.pillar1} weight="35%" testId="dashboard-pillar-1" action={<WhyTrustPanel testId="dashboard-pillar-1-why" formulaExplanation="Triggered nexus = revenue exceeds state threshold. Score = registered/triggered × 100, minus 12 pts per unregistered triggered state (cap 36)." />} />
            <PillarBar label={<>Privacy Law Readiness <InfoTooltip testId="tooltip-pillar-2" text="Based on the privacy laws enacted in your active jurisdictions and whether your policies address them." />{signalTrust && (
              <span
                className="ml-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded align-middle"
                style={{ background: signalTrust.color, color: "var(--rg-bg)" }}
                title={signalTrust.detail}
                data-testid="dashboard-signal-trust-badge"
              >
                {signalTrust.label}
              </span>
            )}</>} score={report.pillar2} weight="30%" testId="dashboard-pillar-2" action={
              <div className="space-y-2">
                {isPillar2Provisional && (
                  <div className="rounded-md border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-3" data-testid="pillar-2-provisional-banner">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rg-amber)]">Provisional — profile incomplete</div>
                    <p className="text-xs text-[var(--rg-text)] leading-snug mt-1">
                      {promptBProvisionalStates.length > 0 ? (
                        <>
                          Comprehensive privacy scoring is now active for{" "}
                          <span className="font-semibold text-[var(--rg-text-strong)]">
                            {promptBProvisionalStates.map((a) => STATE_NAMES[a] || a).join(", ")}
                          </span>
                          . Your compliance profile is missing three questions we added on July&nbsp;15,&nbsp;2026 — please answer them for a verified score for {promptBProvisionalStates.length === 1 ? "that state" : "these states"}.
                        </>
                      ) : (
                        <>Comprehensive privacy scoring is now available for your selected state(s), but your compliance profile is missing three questions we added on July&nbsp;15,&nbsp;2026. Complete them for a verified score.</>
                      )}
                    </p>
                    <Link to="/onboarding">
                      <Button size="sm" className="mt-2 bg-[var(--rg-amber)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-amber)] font-semibold" data-testid="pillar-2-complete-profile-cta">
                        Complete profile
                      </Button>
                    </Link>
                  </div>
                )}
                <WhyTrustPanel testId="dashboard-pillar-2-why" formulaExplanation="Per-state weighted: TX 1.3×, FL 1.0×, NY 0.5× (SHIELD only), DC 0.5× (no law). 4 rights × 25 pts. TX missing opt-out: -10 pts." uncoveredNote={pillar2UncoveredNote} />
              </div>
            } />
            <PillarBar label={<>Policy Documents <InfoTooltip testId="tooltip-pillar-3" text="Based on the presence and completeness of your public-facing policy documents." /></>} score={report.pillar3} weight="20%" testId="dashboard-pillar-3" action={<WhyTrustPanel testId="dashboard-pillar-3-why" formulaExplanation="Policy Privacy (22), data sharing (18), ToS (15), Cookie (13), Refund (12), Privacy contact (12), Retention (8). Max 100." />} />
            <PillarBar label={<>Exposure Awareness <InfoTooltip testId="tooltip-pillar-4" text="Based on whether you have documented and actively monitored your jurisdiction exposure." /></>} score={report.pillar4} weight="15%" testId="dashboard-pillar-4" action={<WhyTrustPanel testId="dashboard-pillar-4-why" formulaExplanation="Jurisdictions identified (40), nexus documented (30), monitoring active (30)." />} />
          </div>
        </div>

        {/* Quick-Win banner (composite < 60) */}
        {showQuickWin && (
          <Link to="/remediation" className="mt-6 block">
            <div className="rounded-lg border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-5 flex items-start gap-4 hover:bg-[var(--rg-amber-soft)] transition-fast" data-testid="quick-win-banner">
              <div className="rounded-full bg-[var(--rg-amber)] text-[var(--rg-on-sage)] w-10 h-10 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--rg-amber)] mb-1">Quick Win</div>
                <div className="text-[var(--rg-text-strong)] font-semibold">
                  {topRec.action} could improve your score by <span className="text-[var(--rg-amber)] font-mono">+{topRec.potentialGain}</span> points.
                </div>
                <div className="text-xs text-[var(--rg-text)] mt-1">Open Remediation to see how →</div>
              </div>
            </div>
          </Link>
        )}

        {/* Shareable Readiness Badge (composite > 75 + Verified mode) */}
        {showShareBadge && (
          <div className="mt-6 rounded-lg border border-[var(--rg-sage)] bg-[var(--rg-sage)]/5 p-5" data-testid="share-badge-card">
            <div className="flex items-start gap-4">
              <div className="rounded-full bg-[var(--rg-sage)] text-[var(--rg-on-sage)] w-10 h-10 flex items-center justify-center flex-shrink-0">
                <Award className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--rg-sage)] mb-1">Share your readiness level</div>
                <div className="text-[var(--rg-text-strong)] text-sm mb-2">
                  Embed a small badge on your storefront or About page that links back to your readiness profile.
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[var(--rg-bg)] border border-[var(--rg-sage)] text-[var(--rg-sage)] text-xs font-mono mb-3" data-testid="badge-preview">
                  ✓ Compliance readiness checked by Regulens
                </div>
                <div>
                  <Button size="sm" onClick={copyBadgeSnippet} className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" data-testid="copy-badge-btn">
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy embed HTML
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Estimated-mode notice — shown in place of the badge when the user
            would otherwise qualify but is on the Quick path. */}
        {composite > 75 && !isVerifiedScore && ["growth", "professional", "agency", "pro"].includes(tier) && (
          <div className="mt-6 rounded-lg border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-5" data-testid="share-badge-blocked-estimated">
            <div className="flex items-start gap-4">
              <div className="rounded-full bg-[var(--rg-amber)] text-[var(--rg-on-sage)] w-10 h-10 flex items-center justify-center flex-shrink-0">
                <Award className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--rg-amber)] mb-1">
                  Share Badge unavailable in Quick Estimate mode
                </div>
                <div className="text-[var(--rg-text-strong)] text-sm mb-2">
                  Your readiness score is impressive, but it's based on tier ranges (worst-case per state). Switch to <strong>Verified</strong> mode by entering exact per-state numbers to unlock a public shareable badge.
                </div>
                <Link to="/settings" data-testid="share-badge-refine-link">
                  <Button size="sm" className="bg-[var(--rg-amber)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-amber)] font-semibold">
                    Refine to Verified Score
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Referral section — Agency tier only */}
        {tier === "agency" && <ReferralSection referral={referral} variant="report" />}

        {/* P8 — Free-tier upgrade prompt (locked breakdown) */}
        {lockedFreeReport && (
          <div className="mt-10 rounded-lg border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] p-8 text-center" data-testid="pro-paywall">
            <Lock className="w-10 h-10 text-[var(--rg-sage)] mx-auto mb-3" />
            <h3 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-1">Your score is ready</h3>
            <p className="text-[var(--rg-text)] mb-5">Upgrade to Solo ($29/month) to unlock:</p>
            <ul className="text-left max-w-md mx-auto space-y-2 mb-6" data-testid="upgrade-checklist">
              <li className="flex items-start gap-2 text-sm text-[var(--rg-text-strong)]"><span className="text-[var(--rg-sage)] font-bold">✓</span> Full breakdown across all 4 pillars</li>
              <li className="flex items-start gap-2 text-sm text-[var(--rg-text-strong)]"><span className="text-[var(--rg-sage)] font-bold">✓</span> Per-state findings for all your selected jurisdictions</li>
              <li className="flex items-start gap-2 text-sm text-[var(--rg-text-strong)]"><span className="text-[var(--rg-sage)] font-bold">✓</span> Complete remediation plan with priority sorting</li>
              <li className="flex items-start gap-2 text-sm text-[var(--rg-text-strong)]"><span className="text-[var(--rg-sage)] font-bold">✓</span> Verified government source citations for every finding</li>
            </ul>
            <Link to="/billing"><Button size="lg" className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-bold" data-testid="upgrade-cta">Unlock Full Report — $29/month</Button></Link>
            <div className="mt-4 text-xs text-[var(--rg-muted)]">
              Or view our sample report first to see exactly what you get.{" "}
              <Link to="/sample-report" className="text-[var(--rg-sage)] hover:underline" data-testid="upgrade-sample-link">View Sample Report →</Link>
            </div>
          </div>
        )}

        {/* Full Pro content */}
        {!lockedFreeReport && (
          <>
            {report.executiveSummary && (
              <div className="mt-10 rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6" data-testid="executive-summary">
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-2">Executive Summary</div>
                <p className="text-[var(--rg-text)] leading-relaxed">{report.executiveSummary}</p>
              </div>
            )}

            <div className="mt-10">
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">Jurisdiction Analysis</div>
              <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-5">Per-state findings & citations</h2>
              <JurisdictionGrid analysis={report.jurisdictionAnalysis || {}} verifiedSources={report.verifiedSources} testIdPrefix="dashboard-juris" perStateConsumerTier={profile?.per_state_consumer_tier || {}} scoreMode={scoreMode} />
            </div>

            {/* Score history chart */}
            {tier === "pro" && history.length > 0 && (
              <div className="mt-10 rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6" data-testid="score-history">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-[var(--rg-sage)]" />
                  <h2 className="font-heading font-bold text-xl text-[var(--rg-text-strong)]">Score history</h2>
                </div>
                {history.length === 1 ? (
                  <div className="text-sm text-[var(--rg-text)]">Fix an item, run again, watch your score grow. <Link to="/remediation" className="text-[var(--rg-sage)] hover:underline">Open Remediation →</Link></div>
                ) : (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={history.map(h => ({ date: h.audit_date.slice(0,10), score: h.composite_score }))}>
                        <defs>
                          <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="var(--rg-sage)" stopOpacity={0.5}/>
                            <stop offset="95%" stopColor="var(--rg-sage)" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="var(--rg-border)" strokeDasharray="3 3" />
                        <XAxis dataKey="date" stroke="var(--rg-muted)" tick={{ fontSize: 11 }} />
                        <YAxis domain={[0, 100]} stroke="var(--rg-muted)" tick={{ fontSize: 11 }} />
                        <Tooltip contentStyle={{ background: "var(--rg-surface)", border: "1px solid var(--rg-border)", color: "var(--rg-text)" }} labelStyle={{ color: "var(--rg-text-strong)" }} itemStyle={{ color: "var(--rg-text)" }} />
                        <Area type="monotone" dataKey="score" stroke="var(--rg-sage)" fill="url(#g1)" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div className="mt-10"><Disclaimer /></div>
        </>
        )}

        {/* Manage My Jurisdictions modal */}
        <ManageJurisdictionsModal
          open={jurisModalOpen}
          onOpenChange={setJurisModalOpen}
          selected={jurisSelected}
          onToggle={toggleJuris}
          onSelectAll={() => setJurisSelected([...ALL_STATES])}
          onDeselectAll={() => setJurisSelected([])}
          onSave={saveJurisdictions}
          saving={jurisSaving}
        />
      </section>
    </Layout>
  );
}
